import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardFooter } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  BellRing,
  Plus,
  Sparkles,
  TrendingUp,
  LayoutDashboard,
  ChevronRight,
  MapPin,
  Tag,
  Building2,
  TriangleAlertIcon,
} from "lucide-react";
import NotificationService from "../notification/NotificationService";
import NotificationMessageService from "../notification/NotificationMessageService";
import { useUser } from "../../context/UserContext";

// ---------------------------------------------------------------------------
// HTML parser (identique à AlertsHistoryPage)
// ---------------------------------------------------------------------------
function parseEmailHTML(raw) {
  if (!raw) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, "text/html");
  const paragraphs = Array.from(doc.querySelectorAll("p"));

  return paragraphs
    .map((p) => {
      const anchor = p.querySelector("a");
      if (!anchor)
        return { name: null, url: null, aidId: null, description: p.textContent.trim() };
      const url = anchor.getAttribute("href") ?? "";
      const name = anchor.textContent.trim();
      const idMatch = url.match(/\/aide\/(\d+)/);
      const fullText = p.textContent.trim();
      const afterLink = fullText.slice(name.length).replace(/^\s*:\s*/, "").trim();
      return { name, url, aidId: idMatch ? idMatch[1] : null, description: afterLink };
    })
    .filter((a) => a.name || a.description);
}

// ---------------------------------------------------------------------------
// Plan limits (identique à AlertsPage)
// ---------------------------------------------------------------------------
const PLAN_LIMITS = {
  Aucun: 0,
  Basique: 1,
  Standard: 5,
  Premium: 15,
  Pro: 20,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDateShort(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  return d >= startOfWeek;
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------
function StatCard({ label, value, sub, icon: Icon, accentClass }) {
  return (
    <Card className="  bg-card/80  glass-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <p className="text-sm text-muted-foreground">{label}</p>
          {Icon && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accentClass}`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
        </div>
        <p className="text-3xl font-bold text-foreground">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// QuotaBar (identique à AlertsPage)
// ---------------------------------------------------------------------------
function QuotaBar({ used, total }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(total - used, 0);
  const isFull = remaining === 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>
          {used} / {total} veilles utilisées
        </span>
        <span className={isFull ? "text-destructive font-medium" : ""}>
          {remaining} restante{remaining !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-border/50 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isFull ? "bg-destructive" : "bg-primary"
            }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Dashboard
// ---------------------------------------------------------------------------
export default function DashboardPage() {
  const [notifications, setNotifications] = useState([]);
  const [messagesMap, setMessagesMap] = useState({});
  const [loading, setLoading] = useState(true);

  const { user } = useUser();
  const navigate = useNavigate();

  const plan = user?.type_abonnement ?? "Aucun";
  const planLimit = PLAN_LIMITS[plan] ?? 1;
  const first_name = user?.first_name ?? null;

  // Fetch tout en une seule passe pour éviter le double flash de skeleton
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await NotificationService.getNotification();
      const notifs = res.data ?? [];
      setNotifications(notifs);

      if (notifs.length > 0) {
        const results = await Promise.all(
          notifs.map((n) =>
            NotificationMessageService.getMessagesForNotification(n.id)
              .then((r) => ({ id: n.id, data: r.data ?? [] }))
              .catch(() => ({ id: n.id, data: [] }))
          )
        );
        const map = {};
        results.forEach(({ id, data }) => { map[id] = data; });
        setMessagesMap(map);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Flatten all reports (identique à AlertsHistoryPage)
  const allReports = useMemo(() => {
    const list = [];
    notifications.forEach((notif) => {
      (messagesMap[notif.id] ?? []).forEach((msg) => {
        list.push({ message: msg, notification: notif });
      });
    });
    return list.sort(
      (a, b) => new Date(b.message.timestamp) - new Date(a.message.timestamp)
    );
  }, [notifications, messagesMap]);

  // Derived stats
  const totalAids = useMemo(() => {
    return allReports.reduce((acc, { message }) => {
      const aids = parseEmailHTML(message.message);
      return acc + aids.filter((a) => a.name).length;
    }, 0);
  }, [allReports]);

  const aidsThisWeek = useMemo(() => {
    return allReports
      .filter(({ message }) => isThisWeek(message.timestamp))
      .reduce((acc, { message }) => {
        const aids = parseEmailHTML(message.message);
        return acc + aids.filter((a) => a.name).length;
      }, 0);
  }, [allReports]);

  // Aides par notification (pour la répartition)
  const aidsPerNotif = useMemo(() => {
    return notifications
      .map((notif) => {
        const msgs = messagesMap[notif.id] ?? [];
        const count = msgs.reduce((acc, msg) => {
          const aids = parseEmailHTML(msg.message);
          return acc + aids.filter((a) => a.name).length;
        }, 0);
        return { notif, count };
      })
      .filter(({ count }) => count > 0)
      .sort((a, b) => b.count - a.count);
  }, [notifications, messagesMap]);

  // 3 derniers rapports avec au moins une aide
  const recentReports = useMemo(() => {
    return allReports
      .filter(({ message }) => {
        const aids = parseEmailHTML(message.message);
        return aids.some((a) => a.name);
      })
      .slice(0, 3);
  }, [allReports]);

  const usedCount = notifications.length;
  const canCreate = usedCount < planLimit;

  return (
    <div className="min-h-screen app-page-gradient">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 pt-10 md:pt-0 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <LayoutDashboard className="hidden sm:block w-5 h-5 text-primary" />
              <h1 className="text-4xl font-bold text-foreground">
                {first_name ? (
                  <>
                    Bonjour,{" "}
                    <span className="text-primary">{first_name}</span>
                  </>
                ) : (
                  <>
                    Tableau de <span className="text-primary">bord</span>
                  </>
                )}
              </h1>
            </div>
            <p className="text-muted-foreground">
              Vue d'ensemble de vos veilles et opportunités.
            </p>
          </div>
          <Button
            onClick={() =>
              canCreate
                ? navigate(RouterPath.CREATE_ALERT)
                : navigate(RouterPath.BILLING)
            }
            variant={canCreate ? "default" : "destructive"}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            {canCreate ? "Nouvelle veille" : "Mettre à niveau"}
          </Button>
        </div>
        {!loading && plan === "Aucun" && (
          <div className="grid grid-cols-1 gap-4">
            <Card className="glass-card py-10">
              <CardContent className="p-5 flex flex-col items-center text-center gap-4">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2">
                    <TriangleAlertIcon className="w-5 h-5 text-yellow-500 shrink-0" />
                    <h3 className="text-xl font-semibold text-foreground">
                      Aucun abonnement actif
                    </h3>
                  </div>
                  <p className="text-muted-foreground max-w-lg text-sm">
                    Souscrivez à un plan pour créer vos veilles et recevoir des rapports personnalisés.
                  </p>
                </div>
                <Button onClick={() => navigate(RouterPath.BILLING)}>
                  Mettre à niveau
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stat cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 rounded-xl bg-muted/50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Veilles actives"
              value={usedCount}
              sub={`Plan ${plan} · ${planLimit - usedCount} veille${planLimit - usedCount !== 1 ? "s" : ""} restante${planLimit - usedCount !== 1 ? "s" : ""}`}
              icon={BellRing}
              accentClass="bg-primary/10 text-primary"
            />
            <StatCard
              label="Aides trouvées cette semaine"
              value={aidsThisWeek}
              sub={aidsThisWeek === 0 ? "Aucune nouvelle aide" : "Depuis lundi"}
              icon={TrendingUp}
              accentClass="bg-green-500/10 text-green-600 dark:text-green-400"
            />
            <StatCard
              label="Total aides identifiées"
              value={totalAids}
              sub={
                allReports.length > 0
                  ? `Sur ${allReports.length} rapport${allReports.length > 1 ? "s" : ""} reçu${allReports.length > 1 ? "s" : ""}`
                  : "Aucun rapport reçu"
              }
              icon={Sparkles}
              accentClass="bg-violet-500/10 text-violet-600 dark:text-violet-400"
            />
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Veilles actives */}
            <Card className="  bg-card/80  glass-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Veilles actives
                  </p>
                  <button
                    onClick={() => navigate(RouterPath.ALERTS)}
                    className="text-xs text-primary hover:underline flex items-center gap-0.5"
                  >
                    Voir tout <ChevronRight className="w-3 h-3" />
                  </button>
                </div>

                {notifications.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic py-4 text-center">
                    Aucune veille active
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.slice().reverse().slice(0, 5).map((notif) => {
                      const localities = notif.locality
                        ? notif.locality.split(", ")
                        : [];
                      const callTypes = notif.type_of_call
                        ? notif.type_of_call.split(", ")
                        : [];
                      const companyTypes = notif.type_of_company
                        ? notif.type_of_company.split(", ")
                        : [];
                      const msgCount = (messagesMap[notif.id] ?? []).length;

                      return (
                        <div
                          key={notif.id}
                          className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate(RouterPath.ALERTS)}
                        >
                          <BellRing className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {notif.title}
                            </p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                              {localities.length > 0 && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {localities.slice(0, 2).join(", ")}
                                  {localities.length > 2 && ` +${localities.length - 2}`}
                                </span>
                              )}
                              {callTypes.length > 0 && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Tag className="w-3 h-3" />
                                  {callTypes.slice(0, 1).join(", ")}
                                  {callTypes.length > 1 && ` +${callTypes.length - 1}`}
                                </span>
                              )}
                              {companyTypes.length > 0 && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {companyTypes.slice(0, 1).join(", ")}
                                  {companyTypes.length > 1 && ` +${companyTypes.length - 1}`}
                                </span>
                              )}
                            </div>
                          </div>
                          {msgCount > 0 && (
                            <Badge className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                              {msgCount} rapport{msgCount > 1 ? "s" : ""}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                    {notifications.length > 5 && (
                      <div
                        className="w-full text-xs text-muted-foreground transition-colors py-1 flex items-center justify-center gap-1"
                      >
                        <span>···</span>
                        <span>{notifications.length - 5} de plus</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Quota */}
                {/* {notifications.length > 0 && (
                  <div className="mt-4 pt-4 border-t  ">
                    <QuotaBar used={usedCount} total={planLimit} />
                  </div>
                )} */}
              </CardContent>
            </Card>

            {/* Répartition des aides + derniers rapports */}
            <div className="space-y-4">

              {/* Répartition par veille — uniquement si données */}
              {aidsPerNotif.length > 0 && (
                <Card className="  bg-card/80  glass-card">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                      Aides par veille
                    </p>
                    <div className="space-y-3">
                      {aidsPerNotif.map(({ notif, count }) => {
                        const max = totalAids;
                        const pct = max > 0 ? Math.round((count / max) * 100) : 0;
                        return (
                          <div key={notif.id} className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground font-medium truncate max-w-[70%]">
                                {notif.title}
                              </span>
                              <span className="text-muted-foreground">
                                {count} aide{count > 1 ? "s" : ""}
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-border/50 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary/70 transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Derniers rapports avec aides */}
              {recentReports.length > 0 && (
                <Card className="  bg-card/80  glass-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Derniers rapports
                      </p>
                      <button
                        onClick={() => navigate(RouterPath.ALERTS_HISTORY)}
                        className="text-xs text-primary hover:underline flex items-center gap-0.5"
                      >
                        Historique <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {recentReports.map(({ message, notification }) => {
                        const aids = parseEmailHTML(message.message);
                        const aidCount = aids.filter((a) => a.name).length;
                        const firstAid = aids.find((a) => a.name);
                        return (
                          <div
                            key={`${notification.id}-${message.id}`}
                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => navigate(RouterPath.ALERTS_HISTORY)}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                <span className="text-foreground">
                                  {aidCount} aide{aidCount > 1 ? "s" : ""}
                                </span>
                                {firstAid?.name && ` · ${firstAid.name}`}
                              </p>
                            </div>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {formatDateShort(message.timestamp)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* État vide si rien */}
              {aidsPerNotif.length === 0 && recentReports.length === 0 && !loading && (
                <Card className="border-border/30 border-dashed bg-card/40">
                  <CardContent className="py-10 flex flex-col items-center text-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Aucun rapport reçu pour l'instant
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                        Vos premières aides identifiées apparaîtront ici après
                        le prochain traitement de vos veilles.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Skeleton loading pour les cards du bas */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-64 rounded-xl bg-muted/50 animate-pulse" />
            <div className="space-y-4">
              <div className="h-32 rounded-xl bg-muted/50 animate-pulse" />
              <div className="h-28 rounded-xl bg-muted/50 animate-pulse" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}